// Frontend Voucher Service
// Communicates with backend voucher API

import { getBackendUrl } from '../utils/backendUrl';

export interface VoucherBalance {
  hasVoucherCredits: boolean;
  totalCredits: number;
  voucherCount: number;
}

export interface VoucherTradeResult {
  success: boolean;
  signature?: string;
  solSpent?: number;
  solReceived?: number;
  tokensReceived?: number;
  tokensSold?: number;
  creditsRemaining?: number;
  error?: string;
}

export interface PromoClaimResult {
  success: boolean;
  data?: {
    voucherId: string;
    solCreditsAwarded: number;
    message: string;
  };
  error?: string;
}



class VoucherService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBackendUrl();
  }

  // Get user's voucher balance by wallet address (no auth required)
  async getVoucherBalance(walletAddress: string): Promise<VoucherBalance> {
    try {
      const url = `${this.baseUrl}/api/voucher/balance/${walletAddress}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Origin': window.location.origin
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get voucher balance');
      }

      return {
        hasVoucherCredits: result.hasVoucherCredits,
        totalCredits: result.totalCredits,
        voucherCount: result.voucherCount
      };
    } catch (error) {
      console.error('Failed to get voucher balance:', error);
      return {
        hasVoucherCredits: false,
        totalCredits: 0,
        voucherCount: 0
      };
    }
  }

  // Claim a promo code by wallet address
  async claimPromoCode(walletAddress: string, promoCode: string): Promise<PromoClaimResult> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          walletAddress,
          promoCode
        })
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to claim promo code' };
      }

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('Failed to claim promo code:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to claim promo code' 
      };
    }
  }

  // Execute buy trade with voucher credits
  async executeBuyTrade(
    userId: string, 
    tokenMint: string, 
    solAmount: number, 
    exchanges: any[], 
    authToken: string
  ): Promise<VoucherTradeResult> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/trade/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          userId,
          tokenMint,
          solAmount,
          exchanges
        })
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to execute buy trade' };
      }

      return {
        success: true,
        signature: result.signature,
        solSpent: result.solSpent,
        creditsRemaining: result.creditsRemaining
      };
    } catch (error) {
      console.error('Failed to execute voucher buy trade:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to execute buy trade' 
      };
    }
  }

  // Execute sell trade with voucher credits
  async executeSellTrade(
    userId: string, 
    tokenMint: string, 
    exchanges: any[], 
    authToken: string
  ): Promise<VoucherTradeResult> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/trade/sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          userId,
          tokenMint,
          exchanges
        })
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to execute sell trade' };
      }

      return {
        success: true,
        signature: result.signature,
        solReceived: result.solReceived,
        tokensSold: result.tokensSold
      };
    } catch (error) {
      console.error('Failed to execute voucher sell trade:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to execute sell trade' 
      };
    }
  }

  // Check if user has voucher-bought position for a specific token
  async hasTokenPosition(
    userId: string, 
    tokenMint: string, 
    authToken: string
  ): Promise<{ hasPosition: boolean; tokenBalance: number }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/position/${userId}/${tokenMint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Origin': window.location.origin
        }
      });

      const result = await response.json();

      if (!response.ok) {
        console.warn('Failed to check token position:', result.error);
        return { hasPosition: false, tokenBalance: 0 };
      }

      return {
        hasPosition: result.hasPosition || false,
        tokenBalance: result.position?.token_balance || 0
      };
    } catch (error) {
      console.error('Failed to check token position:', error);
      return { hasPosition: false, tokenBalance: 0 };
    }
  }

  // NEW: Execute voucher buy trade with round integration
  async executeVoucherBuyTradeWithRoundIntegration(
    tradeData: {
      userId: string;
      tokenMint: string;
      solAmount: number;
      exchanges: any[];
      roundId: string;
      userWalletAddress: string;
    },
    authToken: string
  ): Promise<VoucherTradeResult> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/trade/buy-round`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify(tradeData)
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to execute voucher buy trade' };
      }

      return {
        success: true,
        signature: result.signature,
        solSpent: result.solSpent,
        tokensReceived: result.tokensReceived,
        creditsRemaining: result.creditsRemaining
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to execute voucher buy trade' 
      };
    }
  }

  // NEW: Execute voucher sell trade with round integration
  async executeVoucherSellTradeWithRoundIntegration(
    tradeData: {
      userId: string;
      tokenMint: string;
      exchanges: any[];
      roundId: string;
      userWalletAddress: string;
    },
    authToken: string
  ): Promise<VoucherTradeResult> {
    try {
      const response = await fetch(`${this.baseUrl}/api/voucher/trade/sell-round`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Origin': window.location.origin
        },
        body: JSON.stringify(tradeData)
      });

      const result = await response.json();

      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to execute voucher sell trade' };
      }

      return {
        success: true,
        signature: result.signature,
        solReceived: result.solReceived,
        tokensSold: result.tokensSold
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to execute voucher sell trade' 
      };
    }
  }
}

export const voucherService = new VoucherService(); 